/**
 * Session Metrics Calculator
 *
 * Calculates session-wide form scores and ROM analytics from per-rep history.
 * Uses weighted averaging to prevent recency bias.
 */

/**
 * Calculate mean of an array of numbers
 */
function mean(arr) {
  if (!arr || arr.length === 0) return 0;
  return arr.reduce((sum, val) => sum + val, 0) / arr.length;
}

/**
 * Calculate standard deviation
 */
function stdDev(arr) {
  if (!arr || arr.length === 0) return 0;
  const avg = mean(arr);
  const variance = arr.reduce((sum, val) => sum + Math.pow(val - avg, 2), 0) / arr.length;
  return Math.sqrt(variance);
}

/**
 * Calculate median of an array
 */
function median(arr) {
  if (!arr || arr.length === 0) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

/**
 * Calculate linear regression slope (trend)
 * Returns positive if improving, negative if declining
 */
function linearRegressionSlope(yValues) {
  if (!yValues || yValues.length < 2) return 0;

  const n = yValues.length;
  const xValues = Array.from({ length: n }, (_, i) => i);

  const sumX = xValues.reduce((sum, x) => sum + x, 0);
  const sumY = yValues.reduce((sum, y) => sum + y, 0);
  const sumXY = xValues.reduce((sum, x, i) => sum + x * yValues[i], 0);
  const sumXX = xValues.reduce((sum, x) => sum + x * x, 0);

  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  return slope;
}

/**
 * Calculate session-wide form score using peak performance + consistency
 *
 * @param {Array} repHistory - Array of rep objects {rep, formScore, rom, timestamp}
 * @returns {number} Session form score (0-100)
 */
export function calculateSessionFormScore(repHistory) {
  if (!repHistory || repHistory.length === 0) return 0;

  const formScores = repHistory.map(r => r.formScore);
  
  // Get the peak performance (best rep)
  const maxScore = Math.max(...formScores);
  
  // Get the average performance (consistency)
  const avgScore = mean(formScores);
  
  // Get recent performance (last 5 reps or all if less)
  const recentReps = formScores.slice(-Math.min(5, formScores.length));
  const recentAvg = mean(recentReps);
  
  // Final score blends:
  // - Peak ability (what you CAN do) = 50%
  // - Recent performance (current level) = 30%
  // - Average consistency = 20%
  const sessionScore = (maxScore * 0.5) + (recentAvg * 0.3) + (avgScore * 0.2);

  return Math.round(sessionScore);
}

/**
 * Calculate ROM analytics from rep history
 *
 * @param {Array} repHistory - Array of rep objects {rep, formScore, rom, timestamp}
 * @returns {Object} ROM analytics {max, average, median, consistency, trend}
 */
export function calculateROMMetrics(repHistory) {
  if (!repHistory || repHistory.length === 0) {
    return {
      max: 0,
      average: 0,
      median: 0,
      consistency: 0,
      trend: 0
    };
  }

  const roms = repHistory.map(r => r.rom);
  const romAvg = mean(roms);
  const romStd = stdDev(roms);

  // Consistency score: 1 - (coefficient of variation)
  // Higher = more consistent
  // 0 = completely inconsistent, 100 = perfectly consistent
  const consistency = romAvg > 0
    ? Math.round((1 - (romStd / romAvg)) * 100)
    : 0;

  // Trend: positive = improving ROM, negative = declining ROM
  const trend = linearRegressionSlope(roms);

  return {
    max: Math.round(Math.max(...roms)),
    average: Math.round(romAvg),
    median: Math.round(median(roms)),
    consistency: Math.max(0, Math.min(100, consistency)),  // Clamp to 0-100
    trend: Math.round(trend * 10) / 10  // Round to 1 decimal
  };
}

/**
 * Calculate complete session metrics
 *
 * @param {Array} repHistory - Array of rep objects {rep, formScore, rom, timestamp}
 * @returns {Object} Complete session metrics
 */
export function calculateSessionMetrics(repHistory) {
  if (!repHistory || repHistory.length === 0) {
    return {
      formScore: 0,
      rom: {
        max: 0,
        average: 0,
        median: 0,
        consistency: 0,
        trend: 0
      },
      repCount: 0,
      duration: 0
    };
  }

  const formScore = calculateSessionFormScore(repHistory);
  const rom = calculateROMMetrics(repHistory);

  // Calculate session duration
  const firstRep = repHistory[0];
  const lastRep = repHistory[repHistory.length - 1];
  const duration = Math.round((lastRep.timestamp - firstRep.timestamp) / 1000);  // seconds

  return {
    formScore,
    rom,
    repCount: repHistory.length,
    duration
  };
}
